/**
 * ReadWriteSurface - Simple surface showing linked content columns for reader and writer panels
 */
import React, { useEffect, useState, useContext } from 'react';
import styled from 'styled-components';
import { usePanelContext } from 'src/context/PanelContext';
import { useBrainContext } from 'src/context/BrainContext';
import { useNote } from 'src/context/noteCacheHooks';
import { Card } from 'src/components/Cards/Card';
import { Note, isSource } from '@annote/types';
import { colors, dimensions } from '@annote/core/styles';
import { ShownIdsContext } from 'src/context/ShownContext';

const Surface = styled.div`
  position: relative;
  width: 100%;
  height: 100vh;
  overflow: hidden;
`;

const ReaderColumn = styled.div<{ $isVisible: boolean; $left: number; $width: number }>`
  position: fixed;
  top: 0;
  left: ${({ $left }) => `${$left}px`};
  width: ${({ $width }) => `${$width}px`};
  height: 100vh;
  display: ${({ $isVisible }) => ($isVisible ? 'flex' : 'none')};
  flex-direction: column;
  align-items: flex-start;
  padding: 2rem 1rem;
  overflow-y: auto;
  border-left: 1px solid ${colors.lines.link};
  z-index: 98;
`;

const WriterColumn = styled.div<{ $isVisible: boolean; $left: number; $width: number }>`
  position: fixed;
  top: 0;
  left: ${({ $left }) => `${$left}px`};
  width: ${({ $width }) => `${$width}px`};
  height: 100vh;
  display: ${({ $isVisible }) => ($isVisible ? 'flex' : 'none')};
  flex-direction: column;
  align-items: flex-end;
  padding: 2rem 1rem;
  overflow-y: auto;
  border-right: 1px solid ${colors.lines.link};
  z-index: 98;
`;

const CardWrapper = styled.div`
  width: 100%;
  max-width: 35rem;
  margin-bottom: 1rem;
`;

const ReadWriteSurface: React.FC = () => {
  const { panels, leftPanelsWidth, rightPanelsWidth, openPanel, setActiveId } = usePanelContext();
  const { getNotes } = useBrainContext();
  const { shownIds, hide } = useContext(ShownIdsContext);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  const readerActiveId = panels.reader.activeId;
  const writerActiveId = panels.writer.activeId;
  const readerIsOpen = panels.reader.isOpen;
  const writerIsOpen = panels.writer.isOpen;

  // Track window resize for writer column positioning
  useEffect(() => {
    const handleResize = (): void => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Handle shownIds -> route to appropriate panels, then clear
  useEffect(() => {
    const handleShownIds = async (): Promise<void> => {
      if (shownIds.length === 0) return;

      // Get all notes from shown IDs
      const notes = await getNotes(shownIds);

      // Find first source and first takeaway
      const firstSource = notes.find((note) => isSource(note));
      const firstTakeaway = notes.find((note) => note.type === 'takeaway');

      // Open reader with first source if exists
      if (firstSource) {
        openPanel('reader');
        setActiveId('reader', firstSource.id);
      }

      // Open writer with first takeaway if exists
      if (firstTakeaway) {
        openPanel('writer');
        setActiveId('writer', firstTakeaway.id);
      }

      // Clear ALL shownIds - this surface doesn't use shownIds for display
      hide(shownIds);
    };

    handleShownIds();
  }, [shownIds, getNotes, openPanel, setActiveId, hide]);

  // Get active notes
  const readerNote = useNote(readerActiveId || undefined);
  const writerNote = useNote(writerActiveId || undefined);

  // Get backlinks for reader and writer notes
  const [readerBacklinks, setReaderBacklinks] = useState<Note[]>([]);
  const [writerBacklinks, setWriterBacklinks] = useState<Note[]>([]);

  // Fetch reader backlinks
  useEffect(() => {
    const fetchReaderBacklinks = async (): Promise<void> => {
      if (!readerNote || !readerIsOpen) {
        setReaderBacklinks([]);
        return;
      }

      if (readerNote.backlinks && readerNote.backlinks.length > 0) {
        const backlinkNotes = await getNotes(readerNote.backlinks);
        setReaderBacklinks(backlinkNotes);
      } else {
        setReaderBacklinks([]);
      }
    };

    fetchReaderBacklinks();
  }, [readerNote, readerIsOpen, getNotes]);

  // Fetch writer backlinks
  useEffect(() => {
    const fetchWriterBacklinks = async (): Promise<void> => {
      if (!writerNote || !writerIsOpen) {
        setWriterBacklinks([]);
        return;
      }

      if (writerNote.backlinks && writerNote.backlinks.length > 0) {
        const backlinkNotes = await getNotes(writerNote.backlinks);
        setWriterBacklinks(backlinkNotes);
      } else {
        setWriterBacklinks([]);
      }
    };

    fetchWriterBacklinks();
  }, [writerNote, writerIsOpen, getNotes]);

  // Calculate column positions
  // Reader column: right side of reader panel (left-aligned)
  const readerColumnWidth = 35 * dimensions.rem2px; // 35rem (standard card width)
  const readerColumnLeft = readerIsOpen ? leftPanelsWidth : -1000;

  // Writer column: left side of writer panel (right-aligned)
  const writerColumnWidth = 35 * dimensions.rem2px; // 35rem (standard card width)
  const writerColumnLeft = writerIsOpen ? windowWidth - rightPanelsWidth - writerColumnWidth : -1000;

  return (
    <Surface>
      {/* Reader panel linked content column */}
      <ReaderColumn $isVisible={readerIsOpen} $left={readerColumnLeft} $width={readerColumnWidth}>
        {readerBacklinks.map((note) => (
          <CardWrapper key={note.id}>
            <Card note={note} />
          </CardWrapper>
        ))}
      </ReaderColumn>

      {/* Writer panel linked content column */}
      <WriterColumn $isVisible={writerIsOpen} $left={writerColumnLeft} $width={writerColumnWidth}>
        {writerBacklinks.map((note) => (
          <CardWrapper key={note.id}>
            <Card note={note} />
          </CardWrapper>
        ))}
      </WriterColumn>
    </Surface>
  );
};

export default ReadWriteSurface;
